import type { AdminLegalContent, AdminTestimonial } from "@/lib/admin-api-types";
import type { AboutContent, HomeContent } from "@/lib/api-types";
import {
  DEFAULT_ABOUT_CONTENT,
  DEFAULT_HOME_CONTENT,
  mergeAboutLeadership,
  sanitizeStoredHomeHeroImage,
} from "@/lib/site-content-fallbacks";
import { DEFAULT_LEGAL_CONTENT } from "@/lib/legal-content";

const TESTIMONIALS_KEY = "__aja_mock_testimonials__";
const TESTIMONIALS_SEEDED_KEY = "__aja_mock_testimonials_seeded__";
const HOME_CONTENT_KEY = "__aja_mock_home_content__";
const HOME_CONTENT_SEEDED_KEY = "__aja_mock_home_content_seeded__";
const ABOUT_CONTENT_KEY = "__aja_mock_about_content__";
const ABOUT_CONTENT_SEEDED_KEY = "__aja_mock_about_content_seeded__";
const LEGAL_CONTENT_KEY = "__aja_mock_legal_content__";
const LEGAL_CONTENT_SEEDED_KEY = "__aja_mock_legal_content_seeded__";

type MockGlobal = typeof globalThis & {
  [TESTIMONIALS_KEY]?: AdminTestimonial[];
  [TESTIMONIALS_SEEDED_KEY]?: boolean;
  [HOME_CONTENT_KEY]?: HomeContent & { updatedAt?: string | null };
  [HOME_CONTENT_SEEDED_KEY]?: boolean;
  [ABOUT_CONTENT_KEY]?: AboutContent & { updatedAt?: string | null };
  [ABOUT_CONTENT_SEEDED_KEY]?: boolean;
  [LEGAL_CONTENT_KEY]?: AdminLegalContent;
  [LEGAL_CONTENT_SEEDED_KEY]?: boolean;
};

export function getMockTestimonialsArray(): AdminTestimonial[] {
  const g = globalThis as MockGlobal;
  if (!g[TESTIMONIALS_KEY]) g[TESTIMONIALS_KEY] = [];
  return g[TESTIMONIALS_KEY];
}

export function isMockTestimonialsSeeded(): boolean {
  return (globalThis as MockGlobal)[TESTIMONIALS_SEEDED_KEY] ?? false;
}

export function setMockTestimonialsSeeded(seeded: boolean) {
  (globalThis as MockGlobal)[TESTIMONIALS_SEEDED_KEY] = seeded;
}

export function getMockHomeContentStore(): HomeContent & {
  updatedAt?: string | null;
} {
  const g = globalThis as MockGlobal;
  if (!g[HOME_CONTENT_KEY]) {
    g[HOME_CONTENT_KEY] = {
      ...DEFAULT_HOME_CONTENT,
      hero: {
        ...DEFAULT_HOME_CONTENT.hero,
        headingLines: [...DEFAULT_HOME_CONTENT.hero.headingLines],
        heroImage: "",
      },
      impact: {
        ...DEFAULT_HOME_CONTENT.impact,
        metrics: DEFAULT_HOME_CONTENT.impact.metrics.map((m) => ({ ...m })),
      },
      aboutStory: {
        ...DEFAULT_HOME_CONTENT.aboutStory,
        highlightKeywords: [...DEFAULT_HOME_CONTENT.aboutStory.highlightKeywords],
        timeline: DEFAULT_HOME_CONTENT.aboutStory.timeline.map((t) => ({ ...t })),
      },
      donateSection: { ...DEFAULT_HOME_CONTENT.donateSection },
      featuredPrograms: {
        ...DEFAULT_HOME_CONTENT.featuredPrograms,
        slugs: [...DEFAULT_HOME_CONTENT.featuredPrograms.slugs],
      },
      miniGallery: {
        ...DEFAULT_HOME_CONTENT.miniGallery,
        images: DEFAULT_HOME_CONTENT.miniGallery.images.map((img) => ({
          ...img,
        })),
      },
      updatedAt: null,
    };
  }
  const store = g[HOME_CONTENT_KEY];
  store.hero.heroImage = sanitizeStoredHomeHeroImage(store.hero.heroImage);
  return store;
}

export function isMockHomeContentSeeded(): boolean {
  return (globalThis as MockGlobal)[HOME_CONTENT_SEEDED_KEY] ?? false;
}

export function setMockHomeContentSeeded(seeded: boolean) {
  (globalThis as MockGlobal)[HOME_CONTENT_SEEDED_KEY] = seeded;
}

/** Dev/mock only — restore hero and section copy to static defaults. */
export function resetMockHomeContentToSeed(): void {
  const store = getMockHomeContentStore();
  store.hero = {
    ...DEFAULT_HOME_CONTENT.hero,
    headingLines: [...DEFAULT_HOME_CONTENT.hero.headingLines],
    heroImage: "",
  };
  store.eventsSection = { ...DEFAULT_HOME_CONTENT.eventsSection };
  store.testimonialsSection = { ...DEFAULT_HOME_CONTENT.testimonialsSection };
  store.impact = {
    ...DEFAULT_HOME_CONTENT.impact,
    metrics: DEFAULT_HOME_CONTENT.impact.metrics.map((m) => ({ ...m })),
  };
  store.aboutStory = {
    ...DEFAULT_HOME_CONTENT.aboutStory,
    highlightKeywords: [...DEFAULT_HOME_CONTENT.aboutStory.highlightKeywords],
    timeline: DEFAULT_HOME_CONTENT.aboutStory.timeline.map((t) => ({ ...t })),
  };
  store.donateSection = { ...DEFAULT_HOME_CONTENT.donateSection };
  store.featuredPrograms = {
    ...DEFAULT_HOME_CONTENT.featuredPrograms,
    slugs: [...DEFAULT_HOME_CONTENT.featuredPrograms.slugs],
  };
  store.miniGallery = {
    ...DEFAULT_HOME_CONTENT.miniGallery,
    images: DEFAULT_HOME_CONTENT.miniGallery.images.map((img) => ({ ...img })),
  };
  store.updatedAt = new Date().toISOString();
  setMockHomeContentSeeded(true);
}

function cloneAboutContent(): AboutContent & { updatedAt?: string | null } {
  const d = DEFAULT_ABOUT_CONTENT;
  return {
    hero: {
      ...d.hero,
      primaryCta: { ...d.hero.primaryCta },
      secondaryCta: { ...d.hero.secondaryCta },
      stats: d.hero.stats.map((s) => ({ ...s })),
    },
    intro: {
      ...d.intro,
      paragraphs: [...d.intro.paragraphs],
      founderCard: { ...d.intro.founderCard },
    },
    purpose: {
      vision: { ...d.purpose.vision },
      mission: { ...d.purpose.mission },
    },
    story: {
      ...d.story,
      overviewParagraphs: [...d.story.overviewParagraphs],
      timeline: d.story.timeline.map((t) => ({ ...t })),
    },
    values: {
      footer: d.values.footer,
      items: d.values.items.map((i) => ({ ...i })),
    },
    closingCta: {
      ...d.closingCta,
      primary: { ...d.closingCta.primary },
      secondary: { ...d.closingCta.secondary },
    },
    gallery: {
      title: d.gallery.title,
      subtitle: d.gallery.subtitle,
      images: d.gallery.images.map((i) => ({ ...i })),
    },
    leadership: {
      title: d.leadership.title,
      accentWord: d.leadership.accentWord,
      subtitle: d.leadership.subtitle,
      members: d.leadership.members.map((m) => ({ ...m })),
    },
    updatedAt: null,
  };
}

export function getMockAboutContentStore(): AboutContent & {
  updatedAt?: string | null;
} {
  const g = globalThis as MockGlobal;
  if (!g[ABOUT_CONTENT_KEY]) {
    g[ABOUT_CONTENT_KEY] = cloneAboutContent();
  }
  const store = g[ABOUT_CONTENT_KEY];
  // Keep leadership in sync with defaults (e.g. newly added Srilatha).
  store.leadership = mergeAboutLeadership(store.leadership);
  return store;
}

export function isMockAboutContentSeeded(): boolean {
  return (globalThis as MockGlobal)[ABOUT_CONTENT_SEEDED_KEY] ?? false;
}

export function setMockAboutContentSeeded(seeded: boolean) {
  (globalThis as MockGlobal)[ABOUT_CONTENT_SEEDED_KEY] = seeded;
}

export function resetMockAboutContentToSeed(): void {
  const g = globalThis as MockGlobal;
  g[ABOUT_CONTENT_KEY] = {
    ...cloneAboutContent(),
    updatedAt: new Date().toISOString(),
  };
  setMockAboutContentSeeded(true);
}

/** Dev/mock only — remove auto-test rows and restore seed sort order. */
export function resetMockTestimonialsToSeed(): void {
  const g = globalThis as MockGlobal;
  const items = getMockTestimonialsArray();
  const seeds = items.filter((t) => t.id.startsWith("tst_seed_"));
  items.length = 0;
  items.push(...seeds);
  seeds.forEach((t, i) => {
    t.sortOrder = i + 1;
    t.published = true;
  });
  g[TESTIMONIALS_SEEDED_KEY] = seeds.length > 0;
}

function cloneLegalDocument(document: AdminLegalContent["privacyPolicy"]) {
  return {
    ...document,
    sections: document.sections.map((section) => ({ ...section })),
  };
}

export function getMockLegalContentStore(): AdminLegalContent {
  const g = globalThis as MockGlobal;
  if (!g[LEGAL_CONTENT_KEY]) {
    g[LEGAL_CONTENT_KEY] = {
      privacyPolicy: cloneLegalDocument(DEFAULT_LEGAL_CONTENT.privacyPolicy),
      termsOfService: cloneLegalDocument(DEFAULT_LEGAL_CONTENT.termsOfService),
      updatedAt: null,
    };
  }
  return g[LEGAL_CONTENT_KEY];
}

export function isMockLegalContentSeeded(): boolean {
  return (globalThis as MockGlobal)[LEGAL_CONTENT_SEEDED_KEY] ?? false;
}

export function setMockLegalContentSeeded(seeded: boolean) {
  (globalThis as MockGlobal)[LEGAL_CONTENT_SEEDED_KEY] = seeded;
}
